// The canvas: an iframe of the same origin where the content is edited with the styles of the site.
// <div data-pf-root> holds the content; <div class="pf-ui"> holds the editing interface.
import { escapeHtml } from './dom.js';
import canvasCss from '../css/canvas.css';

export const DEFAULT_CANVAS_STYLES = ['https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css'];

export function createCanvas(mount, options, t) {
    const iframe = document.createElement('iframe');
    iframe.className = 'pf-frame';
    iframe.title = t('canvas.title');
    iframe.setAttribute('tabindex', '0');

    const styles = (options.canvasStyles === undefined || options.canvasStyles === null ? DEFAULT_CANVAS_STYLES : [].concat(options.canvasStyles))
        .filter(Boolean)
        .map(href => `<link rel="stylesheet" href="${escapeHtml(href)}">`)
        .join('');
    const base = options.baseUrl || document.baseURI;
    const lang = escapeHtml(options.contentLang || document.documentElement.lang || 'en');
    const dir = options.rtl ? ' dir="rtl"' : '';
    const nonce = options.nonce ? ` nonce="${escapeHtml(options.nonce)}"` : '';
    const custom = options.canvasCss ? `<style${nonce}>${String(options.canvasCss).replace(/<\/style/gi, '<\\/style')}</style>` : '';
    // Scripts of the host for the canvas (a text editor plugged with the textEditor option, for instance).
    const scripts = [].concat(options.canvasScripts || [])
        .filter(Boolean)
        .map(src => `<script src="${escapeHtml(src)}"${nonce}></script>`)
        .join('');

    iframe.srcdoc = `<!doctype html><html lang="${lang}"${dir}><head><meta charset="utf-8">`
        + `<meta name="viewport" content="width=device-width, initial-scale=1"><base href="${escapeHtml(base)}">`
        + `${styles}<style${nonce}>${canvasCss}</style>${custom}${scripts}</head>`
        + `<body class="pf-canvas-body ${escapeHtml(options.bodyClass || '')}">`
        + `<div class="pf-root ${escapeHtml(options.rootClass || '')}" data-pf-root></div>`
        + `<div class="pf-ui" contenteditable="false"></div></body></html>`;

    return new Promise(resolve => {
        iframe.addEventListener('load', () => {
            const doc = iframe.contentDocument;
            const win = iframe.contentWindow;
            doc.execCommand('defaultParagraphSeparator', false, 'p');
            const gridFallback = ensureGrid(doc, options.nonce);
            resolve({
                iframe,
                doc,
                win,
                root: doc.querySelector('[data-pf-root]'),
                ui: doc.querySelector('.pf-ui'),
                gridFallback,
            });
        }, { once: true });
        mount.append(iframe);
    });
}

const BREAKPOINT_MIN = { sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1400 };
const CONTAINER_MAX = { sm: 540, md: 720, lg: 960, xl: 1140, xxl: 1320 };

/** The Bootstrap 5 grid, enough to lay out columns when the site's stylesheet is missing. */
export function fallbackGridCss() {
    const cols = prefix => [
        `.${prefix}{flex:1 0 0%}`,
        `.${prefix}-auto{flex:0 0 auto;width:auto}`,
        ...Array.from({ length: 12 }, (x, i) => `.${prefix}-${i + 1}{flex:0 0 auto;width:${+(((i + 1) / 12) * 100).toFixed(6)}%}`),
    ].join('');
    let css = '.container,.container-fluid,.container-sm,.container-md,.container-lg,.container-xl,.container-xxl{width:100%;padding-right:12px;padding-left:12px;margin-right:auto;margin-left:auto;box-sizing:border-box}'
        + '.row{display:flex;flex-wrap:wrap;margin-right:-12px;margin-left:-12px}.row>*{flex-shrink:0;width:100%;max-width:100%;padding-right:12px;padding-left:12px;box-sizing:border-box}'
        + cols('col') + 'img{max-width:100%;height:auto}';
    for (const [bp, min] of Object.entries(BREAKPOINT_MIN)) {
        const containers = Object.keys(CONTAINER_MAX).filter(key => BREAKPOINT_MIN[key] <= min).map(key => `.container-${key}`);
        css += `@media (min-width:${min}px){${['.container', ...containers].join(',')}{max-width:${CONTAINER_MAX[bp]}px}${cols(`col-${bp}`)}}`;
    }
    return css;
}

/**
 * Checks that the canvas has a Bootstrap grid (a .row lays out its columns side by side). When the
 * stylesheets of the site are missing or wrong, a minimal grid is added so that columns stay columns.
 */
function ensureGrid(doc, nonce) {
    const probe = doc.createElement('div');
    probe.className = 'row';
    probe.style.cssText = 'position:absolute;visibility:hidden;left:-9999px;top:0;width:400px';
    probe.innerHTML = '<div class="col-6"></div>';
    doc.body.append(probe);
    const ok = doc.defaultView.getComputedStyle(probe).display.includes('flex');
    probe.remove();
    if (ok) return false;
    const style = doc.createElement('style');
    style.id = 'pf-grid-fallback';
    if (nonce) style.setAttribute('nonce', nonce);
    style.textContent = fallbackGridCss();
    doc.head.prepend(style);
    return true;
}
